import { useEffect, useRef, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader, useToast } from '@lms/ui';
import { api } from '@/lib/api';
import { rebaseH5pPayload } from '@/lib/h5p-origin';
import { h5pSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import { useH5pAssets } from './useH5pAssets';
import type { H5pEditorInstance } from './types';

interface EditorPayload {
  integration: Record<string, unknown>;
  token: string;
  content?: {
    library: string;
    params: string;
  };
}

interface H5pEditorProps {
  packageId?: string;
  onSaved?: (packageId: string) => void;
  onStatusChange?: (message: string) => void;
  onSavingChange?: (saving: boolean) => void;
}

const ERROR_MESSAGES: Record<string, string> = {
  'content-not-selected': 'Selecciona un tipo de contenido antes de guardar.',
  'missing-title': 'El título es obligatorio.',
  'missing-library': 'No hay librería seleccionada.',
  'missing-params': 'Completa el contenido de la actividad.',
  'missing-params-params': 'Faltan parámetros de la actividad.',
};

const OVERRIDE_CSS = '/css/h5p-editor-lms.css';

export interface H5pEditorHandle {
  save: () => void;
}

/** H5P reemplaza el nodo montado por el iframe; el ref de React puede quedar en un div desconectado. */
function findEditorIframe(container: HTMLDivElement | null): HTMLIFrameElement | null {
  if (!container) return null;

  if (container instanceof HTMLIFrameElement && container.classList.contains('h5p-editor-iframe')) {
    return container;
  }

  const inside = container.querySelector('iframe.h5p-editor-iframe');
  if (inside instanceof HTMLIFrameElement) return inside;

  const fromParent = container.parentElement?.querySelector('iframe.h5p-editor-iframe');
  if (fromParent instanceof HTMLIFrameElement) return fromParent;

  const fromShell = document.querySelector('.lms-h5p-constructor__shell iframe.h5p-editor-iframe');
  return fromShell instanceof HTMLIFrameElement ? fromShell : null;
}

/** H5PEditor.Editor guarda iframeWindow en el evento load; lo re-sincronizamos antes de guardar. */
function syncEditorIframeWindow(editor: H5pEditorInstance, container: HTMLDivElement | null): boolean {
  const iframe = findEditorIframe(container);
  const win = iframe?.contentWindow;
  if (win) {
    editor.iframeWindow = win;
    return Boolean(win.H5PEditor ?? editor.iframeWindow?.H5PEditor);
  }
  return Boolean(editor.iframeWindow?.H5PEditor);
}

function patchGetAjaxUrl(
  editor: Window['H5PEditor'] | undefined,
  token: string,
): void {
  if (!editor?.getAjaxUrl) return;

  type PatchedAjaxUrl = ((action: string, parameters?: Record<string, unknown>) => string) & {
    __lmsPatched?: boolean;
  };

  const current = editor.getAjaxUrl as PatchedAjaxUrl;
  if (current.__lmsPatched) return;

  const original = current.bind(editor);
  const patched: PatchedAjaxUrl = function (action, parameters) {
    if (parameters === undefined || parameters === null) {
      return original(action, token ? { token } : undefined);
    }
    const params =
      typeof parameters === 'object' && !Array.isArray(parameters)
        ? { ...parameters }
        : {};
    if (token && !params.token) params.token = token;
    return original(action, params);
  };
  patched.__lmsPatched = true;
  editor.getAjaxUrl = patched;
}

function patchEditorIframe(
  iframe: HTMLIFrameElement | null,
  editorToken: string,
  editor?: H5pEditorInstance | null,
) {
  if (!iframe) return;

  iframe.style.width = '100%';
  iframe.style.maxWidth = 'none';
  iframe.style.display = 'block';

  const applyDocOverrides = () => {
    const doc = iframe.contentDocument;
    const win = iframe.contentWindow;
    if (!doc) return;

    if (win?.H5PEditor && editor) {
      editor.iframeWindow = win;
    }

    if (!doc.getElementById('lms-h5p-editor-overrides')) {
      const link = doc.createElement('link');
      link.id = 'lms-h5p-editor-overrides';
      link.rel = 'stylesheet';
      link.href = new URL(OVERRIDE_CSS, window.location.origin).href;
      doc.head.appendChild(link);
    }

    patchGetAjaxUrl(win?.H5PEditor, editorToken);

    if (doc.documentElement) {
      doc.documentElement.style.maxWidth = 'none';
      doc.documentElement.style.width = '100%';
    }
    if (doc.body) {
      doc.body.style.maxWidth = 'none';
      doc.body.style.width = '100%';
    }
  };

  iframe.addEventListener('load', applyDocOverrides);
  applyDocOverrides();
}

export const H5pEditor = forwardRef<H5pEditorHandle, H5pEditorProps>(function H5pEditor(
  { packageId, onSaved, onStatusChange, onSavingChange },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<H5pEditorInstance | null>(null);
  const initializedRef = useRef(false);
  const { toast } = useToast();

  const { data: rawData, isLoading, error } = useQuery({
    queryKey: ['h5p', 'constructor', packageId ?? 'new'],
    queryFn: () =>
      api<EditorPayload>(`/h5p/constructor${packageId ? `/${packageId}` : ''}`),
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const data = useMemo(
    () => rebaseH5pPayload(rawData, window.location.origin),
    [rawData],
  );

  const editorCfg = (data?.integration?.editor ?? {}) as Record<string, unknown>;
  const assets = (editorCfg.assets ?? {}) as { css?: string[]; js?: string[] };
  useH5pAssets(assets.css ?? [], assets.js ?? []);

  const setStatus = useCallback(
    (message: string) => {
      onStatusChange?.(message);
    },
    [onStatusChange],
  );

  const setSaving = useCallback(
    (saving: boolean) => {
      onSavingChange?.(saving);
    },
    [onSavingChange],
  );

  useEffect(() => {
    if (!data?.integration || !containerRef.current) return;

    window.H5PIntegration = data.integration;
    initializedRef.current = false;
    editorRef.current = null;

    let observer: MutationObserver | undefined;
    let resizeHandler: (() => void) | undefined;
    let t1 = 0;
    let t2 = 0;

    const tryInit = () => {
      if (initializedRef.current || !containerRef.current) return true;
      if (!window.H5P || !window.H5PEditor?.Editor) return false;

      const cfg = window.H5PIntegration?.editor as Record<string, unknown> | undefined;
      if (!cfg?.assets) return false;

      window.H5PEditor.$ = window.H5P.jQuery;
      window.H5PEditor.basePath = String(cfg.libraryUrl ?? '');
      window.H5PEditor.fileIcon = cfg.fileIcon;
      window.H5PEditor.ajaxPath = String(cfg.ajaxPath ?? '').endsWith('/')
        ? String(cfg.ajaxPath)
        : `${String(cfg.ajaxPath)}/`;
      window.H5PEditor.filesPath = String(cfg.filesPath ?? '');
      window.H5PEditor.apiVersion = cfg.apiVersion;
      window.H5PEditor.contentLanguage = String(cfg.language ?? 'es');
      window.H5PEditor.copyrightSemantics = cfg.copyrightSemantics;
      window.H5PEditor.metadataSemantics = cfg.metadataSemantics;
      window.H5PEditor.assets = cfg.assets;
      window.H5PEditor.baseUrl = '';
      window.H5PEditor.enableContentHub = Boolean(cfg.enableContentHub);

      const editorToken = String(cfg.token ?? '');
      patchGetAjaxUrl(window.H5PEditor, editorToken);

      const library = data.content?.library ?? '';
      const params = data.content?.params ?? '{}';
      editorRef.current = new window.H5PEditor.Editor(library, params, containerRef.current) as H5pEditorInstance;
      initializedRef.current = true;

      const watchEditorIframe = () => {
        patchEditorIframe(
          findEditorIframe(containerRef.current),
          editorToken,
          editorRef.current as H5pEditorInstance | null,
        );
      };

      watchEditorIframe();
      observer = new MutationObserver(watchEditorIframe);
      observer.observe(containerRef.current, { childList: true, subtree: true });
      resizeHandler = watchEditorIframe;
      window.addEventListener('resize', resizeHandler);
      t1 = window.setTimeout(watchEditorIframe, 250);
      t2 = window.setTimeout(watchEditorIframe, 1000);
      return true;
    };

    const timer = window.setInterval(() => {
      if (tryInit()) window.clearInterval(timer);
    }, 200);

    return () => {
      window.clearInterval(timer);
      observer?.disconnect();
      if (resizeHandler) window.removeEventListener('resize', resizeHandler);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      initializedRef.current = false;
      editorRef.current = null;
    };
  }, [data]);

  const handleSave = useCallback(() => {
    const editor = editorRef.current as H5pEditorInstance | null;
    if (!editor) return;

    if (!syncEditorIframeWindow(editor, containerRef.current)) {
      toast({
        title: 'El editor H5P aún no está listo. Espera a que cargue el formulario.',
        variant: 'danger',
      });
      return;
    }

    setStatus('');
    setSaving(true);

    editor.getContent(
      async (content: { library: string; params: string; title: string }) => {
        try {
          const saved = await api<{ id: string }>('/h5p/constructor/save', {
            method: 'POST',
            body: {
              title: content.title,
              library: content.library,
              parameters: content.params,
              packageId,
            },
          });
          toast(h5pSuccessToast(packageId ? 'edit' : 'create'));
          onSaved?.(saved.id);
        } catch (error) {
          toast(roleActionErrorToast(error, packageId ? 'No se pudo guardar el H5P' : 'No se pudo crear el H5P'));
        } finally {
          setSaving(false);
        }
      },
      (errorCode: string) => {
        toast({
          title: ERROR_MESSAGES[errorCode] ?? 'Revisa los campos obligatorios del editor.',
          variant: 'danger',
        });
        setSaving(false);
      },
    );
  }, [onSaved, packageId, setSaving, setStatus, toast]);

  useImperativeHandle(ref, () => ({ save: handleSave }), [handleSave]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader />
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-red-600">{(error as Error).message}</p>;
  }

  return <div ref={containerRef} id="h5p-editor-container" className="lms-h5p-constructor__editor" />;
});
